/**
 * Services grid — list and management of job service definitions.
 *
 * Server-backed via EntityGrid over odl-app/services/. Columns come from the
 * backend grid-spec descriptor; FE field overrides preserve the child-services
 * clickable chip. Inline cell editing (name, type, flags) is wired via
 * options.gridProps so edits patch the service in place without a dialog.
 * New-service and delete dialogs are preserved and wired to refresh the grid
 * after mutation. Child-services assignment dialog opens from the chip cell.
 *
 * Contains:
 * - ChildServicesDialog — assign/remove child services for a parent service
 * - ServicesGrid        — EntityGrid over odl-app/services/ (default export)
 *
 * ServiceForm (new-service create form) lives in ../forms/ServiceForm.jsx.
 */

import DeleteIcon from '@mui/icons-material/Delete';
import { Autocomplete, Box, Chip, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import _ from 'lodash';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import FilterBar from '@sophocles/components/FilterBar';
import ResponsiveDialog from '@sophocles/components/ResponsiveDialog';
import { EntityGrid } from '@sophocles/data-grids';
import { useGridSessionKey } from '@sophocles/data-grids/datasource/useGridPreferenceKey';
import useGridRefreshRef from '@sophocles/data-grids/datasource/useGridRefreshRef';
import {
  useLazyListJobServicesQuery,
  usePatchServiceMutation,
  useRemoveServiceMutation,
} from '@sophocles/features/entities/jobServices';
import { addToast } from '@sophocles/features/toast';
import useDeleteConfirm from '@sophocles/hooks/useDeleteConfirm';
import usePersistedState from '@sophocles/hooks/usePersistedState';
import { serviceTypeOptions } from '@sophocles/views/services/utils/serviceTypes';

import ServiceForm from '../forms/ServiceForm';

function ChildServicesDialog({ data, open, onClose, onSubmit }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const serviceId = _.get(data, 'serviceid', null) ?? _.get(data, 'id', null);

  const [patchService] = usePatchServiceMutation();
  const [fetchJobServices] = useLazyListJobServicesQuery();

  const [availableServices, setAvailableServices] = useState([]);
  const [selectedChildServices, setSelectedChildServices] = useState([]);

  useEffect(() => {
    if (!open) return;
    fetchJobServices({ limit: 200 }).then(response => {
      if (response.data?.results) {
        const filtered = response.data.results.filter(s => (s.serviceid ?? s.id) !== serviceId);
        setAvailableServices(filtered);
      }
    });
  }, [fetchJobServices, serviceId, open]);

  useEffect(() => {
    const childServices = _.get(data, 'child_services', []);
    const childServiceIds = childServices.map(child => child.serviceid ?? child.id ?? child);
    setSelectedChildServices(availableServices.filter(s => childServiceIds.includes(s.serviceid ?? s.id)));
  }, [data, availableServices]);

  const handleSave = async () => {
    const ids = selectedChildServices.map(s => s.serviceid ?? s.id ?? s);
    const result = await patchService({ id: serviceId, body: { child_services: ids } });
    if (result.error) {
      dispatch(
        addToast({
          title: t('projectServices.services.toast.error'),
          message: t('projectServices.services.toast.failedToUpdateChildServices'),
          level: 'error',
          dismissible: true,
        }),
      );
    } else {
      dispatch(
        addToast({
          title: t('projectServices.services.toast.success'),
          message: t('projectServices.services.toast.childServicesUpdated'),
          level: 'success',
          dismissible: true,
        }),
      );
      if (onSubmit) onSubmit();
      if (onClose) onClose();
    }
  };

  return (
    <ResponsiveDialog
      title={t('projectServices.services.dialog.editChildServicesTitle')}
      open={open}
      onClose={onClose}
      onSubmit={handleSave}
      canSubmit
    >
      <Box sx={{ py: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('projectServices.services.childServicesDialog.description', {
            name: _.get(data, 'name', ''),
          })}
        </Typography>
        <Autocomplete
          multiple
          options={availableServices}
          getOptionLabel={option => `${option.name} (${option.service_type})`}
          value={selectedChildServices}
          onChange={(e, newValue) => setSelectedChildServices(newValue)}
          isOptionEqualToValue={(option, value) =>
            (option.serviceid ?? option.id) === (value.serviceid ?? value.id ?? value)
          }
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="outlined"
                label={`${option.name} (${option.service_type})`}
                {...getTagProps({ index })}
                key={option.serviceid ?? option.id}
              />
            ))
          }
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              placeholder={t('projectServices.services.childServicesDialog.selectPlaceholder')}
            />
          )}
        />
      </Box>
    </ResponsiveDialog>
  );
}

function ServicesGrid() {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [patchService] = usePatchServiceMutation();
  const [removeService] = useRemoveServiceMutation();

  const { onGridReady: handleGridReady, refresh: refreshGrid } = useGridRefreshRef();
  const [isNewDialogOpen, setIsNewDialogOpen] = useState(false);
  const [childDialogData, setChildDialogData] = useState(null);
  const { confirmDelete, dialogElement: deleteConfirmDialog } = useDeleteConfirm();

  // The catalog is long and mostly stable, so it is read by narrowing:
  // what kind of work it is, and whether it is still offered.
  const [typeFilter, setTypeFilter] = usePersistedState(useGridSessionKey('servicesTable', 'typeFilter'), null);
  const [visibilityFilter, setVisibilityFilter] = usePersistedState(
    useGridSessionKey('servicesTable', 'visibility'),
    null,
  );

  const extraParams = useMemo(() => {
    const params = {};
    if (typeFilter) params.service_type = typeFilter;
    if (visibilityFilter === 'visible') params.is_visible = true;
    else if (visibilityFilter === 'hidden') params.is_visible = false;
    else if (visibilityFilter === 'disabled') params.is_enabled = false;
    return params;
  }, [typeFilter, visibilityFilter]);

  const quickFilters = useMemo(
    () => [
      {
        key: 'service_type',
        label: t('projectServices.services.columns.serviceType', 'Type'),
        type: 'single',
        allLabel: t('projectServices.servicePrices.types.all', 'All Types'),
        options: serviceTypeOptions(t),
        value: typeFilter,
        onChange: value => setTypeFilter(value ?? null),
      },
      {
        key: 'visibility',
        label: t('general.status'),
        type: 'single',
        allLabel: t('general.all', 'All'),
        options: [
          { value: 'visible', label: t('projectServices.services.filter.visible', 'Visible') },
          { value: 'hidden', label: t('projectServices.services.filter.hidden', 'Hidden') },
          { value: 'disabled', label: t('projectServices.services.filter.disabled', 'Disabled') },
        ],
        value: visibilityFilter,
        onChange: value => setVisibilityFilter(value ?? null),
      },
    ],
    [t, typeFilter, visibilityFilter, setTypeFilter, setVisibilityFilter],
  );

  const handleResetAll = useCallback(() => {
    setTypeFilter(null);
    setVisibilityFilter(null);
  }, [setTypeFilter, setVisibilityFilter]);

  const toolbarContent = <FilterBar quickFilters={quickFilters} onResetAll={handleResetAll} />;

  // child_services chip — clickable, opens the child-services dialog.
  // The other descriptor columns (name, service_type, target_field, description,
  // is_visible, is_enabled, is_included_in_change_order) are driven by the
  // descriptor with editable: true; the inline processRowUpdate below handles
  // the patch on commit.
  const fieldOverrides = useMemo(
    () => ({
      child_services: {
        renderCell: ({ row, value }) => {
          const childCount = value?.length || 0;
          return (
            <Chip
              label={
                childCount === 0
                  ? t('projectServices.services.noChildren')
                  : t('projectServices.services.childCount', { count: childCount })
              }
              size="small"
              variant="outlined"
              color={childCount > 0 ? 'primary' : 'default'}
              onClick={e => {
                e.stopPropagation();
                setChildDialogData(row);
              }}
              sx={{ cursor: 'pointer' }}
            />
          );
        },
      },
    }),
    [t],
  );

  const handleDeleteClick = useCallback(
    row => {
      confirmDelete(row, {
        entityLabel: t('projectServices.services.entityLabel', 'Service'),
        onConfirm: async deletedRow => {
          // Prefer serviceid for mutation route; fall back to id from odl-app descriptor
          const id = deletedRow.serviceid ?? deletedRow.id;
          await removeService({ id });
          dispatch(
            addToast({
              title: t('projectServices.services.toast.success'),
              message: t('projectServices.services.toast.serviceDeleted'),
              level: 'success',
              dismissible: true,
            }),
          );
          refreshGrid();
        },
      });
    },
    [confirmDelete, dispatch, removeService, t, refreshGrid],
  );

  // Delete action — not modeled in the descriptor
  const extraColumns = useMemo(
    () => [
      {
        field: 'actions',
        headerName: '',
        width: 50,
        sortable: false,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        display: 'flex',
        renderCell: ({ row }) => (
          <Tooltip title={t('projectServices.services.tooltip.delete')}>
            <IconButton
              size="small"
              color="error"
              onClick={e => {
                e.stopPropagation();
                handleDeleteClick(row);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [t, handleDeleteClick],
  );

  const processRowUpdate = useCallback(
    async (newRow, oldRow) => {
      const changed = {};
      for (const field of [
        'name',
        'service_type',
        'target_field',
        'description',
        'is_visible',
        'is_enabled',
        'is_included_in_change_order',
      ]) {
        if (newRow[field] !== oldRow[field]) {
          changed[field] = newRow[field];
        }
      }

      if (Object.keys(changed).length === 0) return oldRow;

      // Prefer serviceid for the mutation route; fall back to id from the odl-app descriptor
      const serviceId = newRow.serviceid ?? newRow.id;

      const result = await patchService({ id: serviceId, body: changed });
      if (result.error) {
        dispatch(
          addToast({
            title: t('projectServices.services.toast.error'),
            message: t('projectServices.services.toast.failedToUpdateService'),
            level: 'error',
            dismissible: true,
          }),
        );
        return oldRow;
      }
      return { ...oldRow, ...result.data };
    },
    [dispatch, patchService, t],
  );

  const handleProcessRowUpdateError = useCallback(
    error => {
      dispatch(
        addToast({
          title: t('projectServices.services.toast.error'),
          message: t('projectServices.services.toast.updateFailed', { error: error.message }),
          level: 'error',
          dismissible: true,
        }),
      );
    },
    [dispatch, t],
  );

  return (
    <Fragment>
      <EntityGrid
        basePath="services"
        fieldOverrides={fieldOverrides}
        extraParams={extraParams}
        options={{
          toolbarContent,
          searchPlaceholder: t('projectServices.services.pageTitle'),
          persistKey: 'servicesTable',
          toolbarVariant: 'flat',
          createAction: {
            label: t('projectServices.services.dialog.newTitle'),
            onClick: () => setIsNewDialogOpen(true),
          },
          extraColumns,
          onGridReady: handleGridReady,
          countNoun: t('projectServices.services.countNoun', 'services'),
          gridProps: {
            editMode: 'cell',
            processRowUpdate,
            onProcessRowUpdateError: handleProcessRowUpdateError,
            sx: {
              '& .MuiDataGrid-cell--editable': {
                '&:hover': { outline: '1px solid', outlineColor: 'primary.main', outlineOffset: -1 },
              },
            },
          },
        }}
      />

      <ServiceForm
        open={isNewDialogOpen}
        onClose={() => setIsNewDialogOpen(false)}
        onSubmit={() => {
          setIsNewDialogOpen(false);
          refreshGrid();
        }}
      />
      <ChildServicesDialog
        data={childDialogData}
        open={childDialogData != null}
        onClose={() => setChildDialogData(null)}
        onSubmit={() => {
          setChildDialogData(null);
          refreshGrid();
        }}
      />
      {deleteConfirmDialog}
    </Fragment>
  );
}

export default ServicesGrid;
